import { useEffect } from 'react'
import i18n from 'translations/i18n'
import { Form } from 'react-final-form'
import { Flex, Typography } from '@jisr-hr/ds'
import { Drawer, Button } from '@jisr-hr/design-system'
import { Department, Form as FormValues } from 'redux/newDepartments/types'
import { isRtl } from 'utils/uiHelpers'
import { useDispatch, useSelector } from 'utils/hooks'
import { listDepartmentsLoad } from 'redux/newDepartments/actionCreators'
import DSIcon from 'components/DSIcon'

import DefaultForm from './Form/DefaultForm'
import DeleteDepartment from './DeleteDepartment'
import NewOnboardingForm from './Form/NewOnboardingForm'
import { useDepartmentsContext } from './context'
import styles from '../style.module.css'

export type FormTypes = {
  reset: () => void
}

export type TCompanyData = {
  id: number
  name: string | undefined
  name_ar: string | undefined
  name_i18n: string | undefined
  parent_id: null
}

export type InitialValuesType = {
  child_id?: number
  id?: number
  manager_id?: number
  name?: string
  name_ar?: string
  parent_id?: number
  disableNameFieldes?: boolean
  isCreateSupInFirstLevel?: boolean
}

type AddDepartmentFormProps = {
  data?: Department

  hideParentInput?: boolean
  initialValues?: InitialValuesType
  isOpen: boolean
  onSubmit: (values: FormValues, form: FormTypes) => void
  setIsOpen: (x: boolean) => void
  componyData?: TCompanyData
}

const AddDepartmentForm = ({
  hideParentInput,
  initialValues,
  isOpen,
  onSubmit,
  setIsOpen,
  data,
  componyData,
}: AddDepartmentFormProps): JSX.Element => {
  const dispatch = useDispatch()

  const fetching = useSelector((s) => s.newDepartments.fetching)
  const { isNewOnboarding } = useDepartmentsContext()
  const canDelete = !(data?.number_of_employees !== 0)
  const infoMessage = data?.number_of_employees
    ? i18n.t('move_employees_to_different_department')
    : i18n.t('delete_subdepartments_order_department')
  const titleMessage = data?.number_of_employees
    ? i18n.t('department_has_employees')
    : i18n.t('department_has_subdepartments')

  const onClose = (): void => {
    setIsOpen(false)
    // rest the list and remove filters that applied useEffect
    dispatch(listDepartmentsLoad())
  }

  useEffect(() => {
    if (isOpen && !initialValues?.child_id && initialValues?.id) {
      dispatch(
        listDepartmentsLoad({
          filter: { excluded_self_and_descendants: initialValues?.id },
        }),
      )
    }
  }, [isOpen])

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, form }): JSX.Element => (
        <Drawer
          active={isOpen}
          onClose={(): void => {
            onClose()
            form.reset()
          }}
          title={initialValues?.id ? i18n.t('edit_department') : i18n.t('add_new_department')}
          {...(isNewOnboarding && {
            className: styles.onboardingDrawerBody,
            rootClassName: styles.onboardingDrawerRoot,
            contentClassName: styles.onboardingDrawerContent,
            header: (
              <Flex className={styles.onboardingDrawerHeader}>
                <div />
                <Typography
                  variant="title-1"
                  text={
                    initialValues?.id ? i18n.t('edit_department') : i18n.t('label.new_department')
                  }
                />
                <DSIcon
                  size="lg"
                  style={{ cursor: 'pointer' }}
                  name={isRtl ? 'align-right-01' : 'align-left-01'}
                  onClick={(): void => {
                    onClose()
                    form.reset()
                  }}
                />
              </Flex>
            ),
          })}
          content={
            isNewOnboarding ? (
              <NewOnboardingForm
                componyData={componyData}
                hideParentInput={hideParentInput}
              />
            ) : (
              <DefaultForm hideParentInput={hideParentInput} />
            )
          }
          footer={
            <Flex justifyBetween>
              <Flex style={{ gap: 8 }}>
                <Button
                  label={initialValues?.id ? i18n.t('label.save') : i18n.t('label.add')}
                  size="medium"
                  onClick={(): void => {
                    handleSubmit()
                  }}
                  disabled={fetching}
                />
                <Button
                  label={i18n.t('label.cancel')}
                  size="medium"
                  variant="secondary"
                  onClick={(): void => {
                    onClose()
                    form.reset()
                  }}
                />
              </Flex>
              {!!initialValues?.id && (
                <DeleteDepartment
                  confirmMessage={{
                    canDelete,
                    type: canDelete ? 'danger' : 'info',
                    title: canDelete
                      ? `${i18n.t('label.delete')} ${data?.name_i18n}`
                      : titleMessage,
                    message: canDelete ? i18n.t('confirm_delete_department') : infoMessage,
                    data,
                  }}
                  id={initialValues?.id}
                  onDone={(): void => setIsOpen(false)}
                />
              )}
            </Flex>
          }
        />
      )}
    />
  )
}

export default AddDepartmentForm
